import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Img from 'react-image';
import hash from 'object-hash';
import {
  Flex,
  Typography,
  SelectDropdown,
  Card,
  CardTitle,
  CardContent,
  Box,
  color,
  Button,
  Overlay,
  Loader
} from '@kistech/kis-ui';
import NoImagePreview from 'assets/no_image_preview.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as clientSelectors } from 'store/clients';
import { actions as ciActions, selectors as ciSelectors } from 'store/clientIntegrations';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { ImageUploadOverlay } from '../Shared/ImageUploadOverlay';
import { MediaTypes, MediaResponse, CIMediaProps } from '../../types';

const initialImage = {
  exists: false,
  loading: false,
  width: 0,
  height: 0
};

export const GenreImages: React.FC<CIMediaProps> = (props: CIMediaProps) => {
  const selectPending = useSelector(ciSelectors.selectPending);
  const genres = useSelector(ciSelectors.selectGroupedGenres);
  const { pending, successful } = useSelector(mediaSelectors.selectAction);
  const clientId = useSelector(clientSelectors.selectClientId);
  const [getTicketingEvents, setGenre, getMedia, deleteMedia, resetAction] = useActions([
    ciActions.getTicketingEvents,
    mediaActions.setGenre,
    mediaActions.getMedia,
    mediaActions.deleteMedia,
    mediaActions.resetAction
  ]);

  const selectedGenre = useSelector(mediaSelectors.selectGenre);
  const [image, setImage] = useState<typeof initialImage>(initialImage);
  const genreImages = useSelector(mediaSelectors.selectGenreImages);
  const hasGenres = genres.length > 0;
  const loading = image.loading || pending;
  const [showImageUploadOverlay, setShowImageUploadOverlay] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [imageName, setImageName] = useState<string | undefined>('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  React.useEffect(() => {
    setPreviewUrl('');
    setImageName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (props.environment) {
      setPreviewUrl('');
      setImageName('');
      getTicketingEvents(props.environment.id);
      getMedia({ type: MediaTypes.GENRE, clientIntegrationId: props.environment.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.environment]);

  const getImageProperties = () => {
    setImage({ ...image, loading: true });
    const img = new Image();
    img.src = previewUrl;
    img.onload = () => {
      setImage({
        width: img.width,
        height: img.height,
        loading: false,
        exists: true
      });
    };
    img.onerror = () => setImage({ ...image, loading: false, exists: false });
  };

  React.useEffect(() => {
    if (props.environment && successful) {
      setPreviewUrl('');
      setImageName('');
      getMedia({ type: MediaTypes.GENRE, clientIntegrationId: props.environment.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  React.useEffect(() => {
    if (selectedGenre && genreImages && genreImages.length) {
      const baseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/genre_images/`;
      const imgObj = genreImages.find((item: MediaResponse) => {
        return item.name === hash(selectedGenre.replace(new RegExp('/', 'g'), ' '));
      });
      if (imgObj && imgObj.s3Name) {
        setPreviewUrl(baseUrl.concat(imgObj.s3Name));
        setImageName(imgObj.imageName);
      } else {
        setPreviewUrl('');
        setImageName('');
      }
      resetAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenre, genreImages]);

  React.useEffect(() => {
    if (previewUrl) {
      getImageProperties();
    } else {
      setImage({ ...image, exists: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  const onDeleteImage = () => {
    if (genreImages && genreImages.length) {
      const imgObj = genreImages.find((item: MediaResponse) => {
        return item.name === hash(selectedGenre.replace(new RegExp('/', 'g'), ' '));
      });
      if (imgObj && imgObj.id) {
        deleteMedia(imgObj.id);
      }
    }
    setShowConfirmation(false);
  };

  return (
    <>
      {showConfirmation && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={400}>
              <Typography size={40} weight={600} align="center" style={{ lineHeight: 1 }}>
                Are You Sure?
              </Typography>
            </Box>
          }
          description={
            <Box width={500}>
              <Typography size={24} align="center">
                Are you sure you want to delete this image?
              </Typography>
            </Box>
          }
          confirmText="Yes"
          onConfirm={onDeleteImage}
          cancelText="Cancel"
          onCancel={() => setShowConfirmation(false)}
          height={300}
          appearance="error"
        />
      )}
      {(selectPending || pending) && (
        <Overlay>
          <Loader color={color.BLACK} />
        </Overlay>
      )}
      <Flex direction="column" fullWidth noWrap>
        {props.environment && hasGenres && (
          <>
            <Flex align="center" margin="0 0 6" direction="row">
              <Typography size={24} weight={700} style={{ marginRight: '20px' }}>
                <Box width={180}>Select a Genre</Box>
              </Typography>
              <SelectDropdown
                items={genres.sort()}
                itemToString={(item: string) => item}
                selectedItem={selectedGenre}
                onChange={setGenre}
                placeholder="Select a genre"
              />
            </Flex>
            <Flex fullWidth direction="row">
              <Box width={650}>
                <Card raised hasBorderRadius>
                  <Card.Title
                    title={
                      <Typography size={18} weight={700}>
                        Genre Image
                      </Typography>
                    }
                    gutter={5}
                  />
                  {image.exists ? (
                    <>
                      <Flex
                        fullWidth
                        height={50}
                        align="center"
                        direction="row"
                        style={{ backgroundColor: color.X_LIGHT_GRAY }}
                      >
                        <Box fullHeight width={8} margin="0 3 0 0" style={{ backgroundColor: color.PRIMARY }} />
                        <Typography size={18}>{selectedGenre}</Typography>
                      </Flex>
                      <Card.Content gutter={5}>
                        <Flex fullWidth>
                          <Box width="45%" padding="0 2 0 0">
                            <Box margin="0 0 2">
                              <Typography size={18}>File Details</Typography>
                            </Box>
                            {loading ? (
                              <Typography>Fetching details...</Typography>
                            ) : (
                              <>
                                {imageName && <Typography size={14}>Name: {imageName}</Typography>}
                                <Typography size={14}>
                                  Size: {image.height}px by {image.width}px
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Flex>
                      </Card.Content>
                    </>
                  ) : (
                    <CardContent>
                      <Box fullWidth margin="0 0 5">
                        <Box margin="0 0 4">
                          <Typography size={18} weight={400}>
                            Add one image to show for the genre
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          width={180}
                          isLoading={loading}
                          isDisabled={!selectedGenre}
                          onClick={() => setShowImageUploadOverlay(true)}
                        >
                          Add Image
                        </Button>
                      </Box>
                    </CardContent>
                  )}
                </Card>
              </Box>
              <Box margin="0 3" width={325}>
                <Card raised hasBorderRadius>
                  <CardTitle
                    title={
                      <Typography size={18} weight={600}>
                        Image Preview
                      </Typography>
                    }
                    gutter={5}
                  />
                  <CardContent>
                    <Flex padding="2" align="center" justify="center">
                      <Flex width={200} height={150} align="center" justify="center">
                        {loading ? (
                          <Flex height={252} align="center">
                            <Typography>Fetching image...</Typography>
                          </Flex>
                        ) : (
                          <Img
                            src={previewUrl}
                            width="100%"
                            height="100%"
                            style={{ objectFit: 'cover' }}
                            unloader={<img alt="preview" src={NoImagePreview} width={180} height={158} />}
                          />
                        )}
                      </Flex>
                      <Box height={20} fullWidth />
                      {image.exists && (
                        <>
                          <Button
                            size="small"
                            appearance="primary"
                            width={250}
                            isLoading={loading}
                            onClick={() => setShowImageUploadOverlay(true)}
                          >
                            Select Different Image
                          </Button>
                          <Box fullWidth margin="2 0" />
                          <Button
                            size="small"
                            width={250}
                            appearance="white"
                            onClick={() => setShowConfirmation(true)}
                            isLoading={loading}
                            data-testid="delete-video-button"
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </Flex>
                  </CardContent>
                </Card>
              </Box>
            </Flex>
          </>
        )}
      </Flex>
      {showImageUploadOverlay && (
        <ImageUploadOverlay
          imageType="genre"
          imageName={selectedGenre}
          clientIntegrationId={props.environment && props.environment.id}
          onCancel={() => setShowImageUploadOverlay(false)}
          onSuccess={() => setShowImageUploadOverlay(false)}
        />
      )}
    </>
  );
};
